<script setup lang="ts">
import { useToast } from 'vue-toastification'
import { defineComponent, h } from 'vue'
import type { FaqItem } from '@/types'
import { FAQAccordion } from '#components'

const props = defineProps<{
  showTabs?: boolean
  for?: string
}>()

const { $getLocale, $ts } = useI18n()
const toast = useToast()

const { data, error } = await useFetch<FaqItem[]>('/api/faq', {
  method: 'GET',
  query: { for: props.for },
})
if (error.value)
  toast.error($ts('components.contactForm.errorMessage'))

const getYoutubeEmbedUrl = (url?: string | null) => {
  if (!url) return undefined

  if (url.includes('/embed/')) return url

  const regex = /(?:youtube\.com\/(?:watch\?v=|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  const match = url.match(regex)

  if (match && match[1]) {
    return `https://www.youtube.com/embed/${match[1]}`
  }

  return url
}

const items = computed(() => {
  return data.value?.flatMap(i => {
    const translations = i.translations.filter(t => t.bitbeli_languages_code === $getLocale())
    const category = i.category.translations.find(c => c.bitbeli_languages_code === $getLocale())
    return translations.map(t => ({
      question: t.question,
      answer: t.answer,
      category: category?.name || '',
      youtube: getYoutubeEmbedUrl(t.youtube),
    }))
  }) ?? []
})

const groupedItems = computed(() => {
  const groups = items.value.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = []
    }
    acc[item.category].push(item)
    return acc
  }, {} as Record<string, typeof items.value>)
  return groups
})

// Workaround using Vnodes to be able to use FTabs component
const tabs = computed(() => {
  return Object.keys(groupedItems.value).map(category => ({
    title: category,
    component: defineComponent({
      render () {
        return h('div', groupedItems.value[category].map(item =>
          h('div', { key: item.question, id: item.question, class: 'flex flex-col gap-16px mb-4' }, [
            h(FAQAccordion, { answers: [{ title: item.question, text: item.answer, iframe: item.youtube }], markdown: true, tableName: 'default' }),
          ]),
        ))
      },
    }),
  }))
})

const route = useRoute()
const defaultTabIndex = computed(() => {
  const hash = route.hash.substring(1)
  const categoryIndex = Object.keys(groupedItems.value).findIndex(category =>
    groupedItems.value[category].some(item =>
      item.question.toLowerCase() === hash.toLowerCase(),
    ),
  )
  return categoryIndex
})
</script>

<template>
  <div pt-64px pb-80px fubex-container flex flex-col gap-48px>
    <ClientOnly>
      <FTabs
        v-if="showTabs"
        id="faq"
        :tabs="tabs"
        tab-list-center
        tab-list-class="mt-48px"
        tab-panels-class="mt-46px mb-62px"
        as="div"
        :default-index="defaultTabIndex"
      />
      <template v-else>
        <template v-for="item in items" :key="item">
          <div :id="item.question" flex flex-col gap-16px>
            <h4 heading4>
              {{ item.category }}
            </h4>
            <FAQAccordion :answers="[{ title: item.question, text: item.answer, iframe: item.youtube }]" markdown table-name="default" />
          </div>
        </template>
      </template>
    </ClientOnly>
  </div>
</template>
